"use client";

import {
	RiCloseLine,
	RiStarFill,
	RiStarHalfFill,
	RiStarLine,
} from "@remixicon/react";
import { Button } from "@workspace/ui/components/button";
import { cn } from "@workspace/ui/lib/utils";
import { useCallback, useEffect, useRef, useState } from "react";

interface ReviewStarsProps {
	rating: number;
	onChange: (newRating: number) => void;
	readOnly?: boolean;
}

export function ReviewStars({
	rating,
	onChange,
	readOnly = false,
}: ReviewStarsProps) {
	const [hoverRating, setHoverRating] = useState<number>(0);
	const [activeStarIndex, setActiveStarIndex] = useState<number | null>(null);
	const [isTouchDevice, setIsTouchDevice] = useState(false);
	const [animatedStars, setAnimatedStars] = useState<Set<number>>(new Set());
	const starsRef = useRef<HTMLDivElement>(null);

	// Detect touch device on mount
	useEffect(() => {
		setIsTouchDevice("ontouchstart" in window || navigator.maxTouchPoints > 0);
	}, []);

	// Calculate rating based on star position
	const calculateRating = useCallback(
		(index: number, position: number): number => {
			// Calculate value based on position within the star
			if (position <= 0.3) {
				return (index + 1) * 2 - 1.5; // Lower part of half star
			}
			if (position <= 0.7) {
				return (index + 1) * 2 - 1; // Half star
			}
			return (index + 1) * 2; // Full star
		},
		[],
	);

	const handleStarInteraction = useCallback(
		(index: number, position: number) => {
			if (readOnly) return;

			const starValue = calculateRating(index, position);
			setHoverRating(Math.max(1, Math.min(10, Math.round(starValue))));
		},
		[readOnly, calculateRating],
	);

	const handleStarClick = useCallback(
		(index: number, clickedRating: number) => {
			if (readOnly) return;

			// When clearing rating, use 0 (not -2) to keep other review data
			const newRating = rating === clickedRating ? 0 : clickedRating;
			onChange(newRating);

			// Enhanced visual feedback with star animations
			setHoverRating(0);
			setActiveStarIndex(index);

			// Animate stars up to the clicked rating
			if (newRating > 0) {
				const starsToAnimate = new Set<number>();
				for (let i = 0; i <= Math.floor((newRating - 1) / 2); i++) {
					starsToAnimate.add(i);
				}
				setAnimatedStars(starsToAnimate);

				// Clear animations after delay
				setTimeout(() => {
					setAnimatedStars(new Set());
				}, 600);
			}

			setTimeout(() => {
				setActiveStarIndex(null);
			}, 200);
		},
		[rating, onChange, readOnly],
	);

	// Improved touch handling
	const handleTouchMove = useCallback(
		(e: React.TouchEvent) => {
			if (readOnly) return;

			e.preventDefault();
			const touch = e.touches[0];
			if (!touch || !starsRef.current) return;

			const elements = document.elementsFromPoint(touch.clientX, touch.clientY);
			const starElement = elements.find((el) =>
				el.hasAttribute("data-star-index"),
			);

			if (starElement) {
				const starIndex = Number.parseInt(
					starElement.getAttribute("data-star-index") || "0",
					10,
				);
				const rect = starElement.getBoundingClientRect();
				const position = (touch.clientX - rect.left) / rect.width;
				handleStarInteraction(starIndex, position);
			}
		},
		[readOnly, handleStarInteraction],
	);

	return (
		<div className="flex items-center gap-1">
			{/* Stars container */}
			<div
				className="relative flex gap-1"
				ref={starsRef}
				onTouchMove={handleTouchMove}
				onTouchStart={(e) => e.stopPropagation()}
				onTouchEnd={(e) => {
					e.preventDefault();
					e.stopPropagation();
					if (hoverRating > 0) {
						handleStarClick(activeStarIndex || 0, hoverRating);
					}
					setHoverRating(0);
				}}
				onMouseLeave={() => {
					if (!isTouchDevice && !readOnly) setHoverRating(0);
				}}
				role="radiogroup"
				aria-label="Rating out of 10"
			>
				{[0, 1, 2, 3, 4].map((index) => (
					<div
						key={index}
						className={cn(
							"relative cursor-pointer touch-manipulation",
							"h-11 w-11 md:h-12 md:w-12", // Smaller on mobile, original size on md+
							readOnly && "pointer-events-none",
						)}
						data-star-index={index}
						onMouseMove={(e) => {
							if (isTouchDevice || readOnly) return;
							const rect = e.currentTarget.getBoundingClientRect();
							const x = e.clientX - rect.left;
							const position = x / rect.width;
							handleStarInteraction(index, position);
						}}
						onTouchStart={(e) => {
							if (readOnly) return;
							e.preventDefault();
							e.stopPropagation();
							const rect = e.currentTarget.getBoundingClientRect();
							const touch = e.touches[0];
							if (!touch) return;
							const x = touch.clientX - rect.left;
							const position = x / rect.width;
							handleStarInteraction(index, position);
						}}
						onClick={() => {
							if (hoverRating > 0 && !readOnly) {
								handleStarClick(index, hoverRating);
							}
						}}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								if (hoverRating > 0 && !readOnly) {
									handleStarClick(index, hoverRating);
								}
							}
						}}
						tabIndex={readOnly ? -1 : 0}
						aria-label={`${index + 1} stars`}
						role="radio"
						aria-checked={Math.ceil(rating / 2) === index + 1}
					>
						{/* Base empty star */}
						<RiStarLine
							className={cn(
								"absolute inset-0 flex items-center justify-center",
								"text-muted-foreground/30",
								"transition-all duration-200",
								"m-auto h-[65%] w-[65%]", // Fixed size for consistency
							)}
						/>
						{/* Filled/Half star logic */}
						{(() => {
							const currentDisplayRating =
								hoverRating > 0 ? hoverRating : rating > 0 ? rating : 0;
							if ((index + 1) * 2 <= currentDisplayRating) {
								return (
									<div
										key={`star-full-${index}`}
										className={cn(
											"absolute inset-0 flex items-center justify-center",
											animatedStars.has(index) &&
												"",
										)}
									>
										<RiStarFill
											className={cn(
												"h-[65%] w-[65%] transition-colors duration-150",
												hoverRating > 0 ? "text-yellow-300" : "text-yellow-400",
												activeStarIndex === index && "animate-pulse",
											)}
										/>
									</div>
								);
							}
							if ((index + 1) * 2 - 1 === currentDisplayRating) {
								return (
									<div
										key={`star-half-${index}`}
										className={cn(
											"absolute inset-0 flex items-center justify-center",
											animatedStars.has(index) &&
												"",
										)}
									>
										<RiStarHalfFill
											className={cn(
												"h-[65%] w-[65%] transition-colors duration-150",
												hoverRating > 0 ? "text-yellow-300" : "text-yellow-400",
												activeStarIndex === index && "animate-pulse",
											)}
										/>
									</div>
								);
							}
							return null;
						})()}
					</div>
				))}
			</div>

			{/* Clear button on the right */}
			{rating > 0 && !readOnly && (
				<Button
					variant="ghost"
					size="sm"
					className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
					onClick={() => onChange(0)}
					aria-label="Clear rating"
				>
					<RiCloseLine className="h-4 w-4" />
				</Button>
			)}

			{/* Screen reader text */}
			<span className="sr-only">{`Rating: ${rating} out of 10`}</span>
		</div>
	);
}
